/**
 * ChangeQuantityModal - Simple modal to change quantity of legacy subscriptions
 */
import { useState, useEffect, useRef } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Stack,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import { QuizButton } from "./shared/QuizButton";
import { QUIZ } from "@/theme/quizTokens";

type ChangeQuantityModalProps = {
  open: boolean;
  onClose: (success?: boolean) => void;
  productTitle: string;
  variantTitle: string;
  currentQuantity: number;
  pricePerUnit: number;
  onConfirm: (newQuantity: number) => Promise<void>;
};

export function ChangeQuantityModal({
  open,
  onClose,
  productTitle,
  variantTitle,
  currentQuantity,
  pricePerUnit,
  onConfirm,
}: ChangeQuantityModalProps) {
  const [quantity, setQuantity] = useState(currentQuantity);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Track previous open state to only reset on actual modal open
  const prevOpenRef = useRef(false);

  // Reset state only when modal opens (not on data changes while open)
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    // Only reset when transitioning from closed to open
    if (open && !wasOpen) {
      setQuantity(currentQuantity);
      setError(null);
      setSuccess(false);
      setConfirming(false);
    }
  }, [open, currentQuantity]);

  const handleIncrement = () => {
    setQuantity((q) => q + 1);
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity((q) => q - 1);
    }
  };

  const handleConfirm = async () => {
    if (quantity === currentQuantity) {
      setError("Please change the quantity before confirming");
      return;
    }

    setConfirming(true);
    setError(null);

    try {
      await onConfirm(quantity);
      setSuccess(true);
      setTimeout(() => {
        onClose(true);
      }, 1500);
    } catch (err: any) {
      setError(err?.message || "Failed to update quantity");
    } finally {
      setConfirming(false);
    }
  };

  const formatPrice = (price: number) => `£${price.toFixed(2)}`;
  const newTotalPrice = quantity * pricePerUnit;
  const currentTotalPrice = currentQuantity * pricePerUnit;
  const priceDiff = newTotalPrice - currentTotalPrice;

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : () => onClose(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "16px" },
          maxHeight: { xs: "100%", sm: "90vh" },
          height: { xs: "100%", sm: "auto" },
          m: { xs: 0, sm: 2 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: { xs: 2, md: 3 },
          borderBottom: "1px solid rgba(0,0,0,.08)",
          background: QUIZ.color.cream,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #E1F0FF 0%, #A5D4FF 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InventoryIcon sx={{ fontSize: 22, color: "#1A57D3" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 16, md: 18 },
                fontWeight: 800,
                color: QUIZ.color.brown,
              }}
            >
              Change Quantity
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 11, md: 12 },
                color: QUIZ.color.brown,
                opacity: 0.65,
              }}
            >
              {productTitle}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={() => onClose(false)}
          disabled={confirming}
          sx={{
            color: QUIZ.color.brown,
            "&:hover": { background: "rgba(0,0,0,.05)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: { xs: 2, md: 3 } }}>
        {/* Success State */}
        {success && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{
              borderRadius: "12px",
              mb: 2,
              "& .MuiAlert-icon": { alignItems: "center" },
            }}
          >
            Quantity updated successfully!
          </Alert>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{
              borderRadius: "12px",
              mb: 2,
              "& .MuiAlert-icon": { alignItems: "center" },
            }}
          >
            {error}
          </Alert>
        )}

        {!success && (
          <>
            {/* Product Info */}
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: QUIZ.color.cream,
                border: "1px solid rgba(0,0,0,.06)",
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: QUIZ.color.brown,
                  mb: 0.5,
                }}
              >
                {productTitle}
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: QUIZ.color.brown,
                  opacity: 0.65,
                }}
              >
                {variantTitle} • {formatPrice(pricePerUnit)} each
              </Typography>
            </Box>

            {/* Quantity Selector */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                py: 3,
              }}
            >
              <IconButton
                onClick={handleDecrement}
                disabled={quantity <= 1 || confirming}
                sx={{
                  width: 56,
                  height: 56,
                  border: "2px solid",
                  borderColor: quantity <= 1 ? "#ccc" : QUIZ.color.brown,
                  color: quantity <= 1 ? "#ccc" : QUIZ.color.brown,
                  "&:hover": {
                    background: "rgba(123,51,6,0.08)",
                  },
                  "&:disabled": {
                    borderColor: "#ccc",
                    color: "#ccc",
                  },
                }}
              >
                <RemoveIcon fontSize="large" />
              </IconButton>

              <Typography
                sx={{
                  fontSize: 48,
                  fontWeight: 900,
                  color: QUIZ.color.brown,
                  minWidth: 80,
                  textAlign: "center",
                }}
              >
                {quantity}
              </Typography>

              <IconButton
                onClick={handleIncrement}
                disabled={confirming}
                sx={{
                  width: 56,
                  height: 56,
                  background: QUIZ.color.brown,
                  color: QUIZ.color.white,
                  "&:hover": {
                    background: "#5A2404",
                  },
                }}
              >
                <AddIcon fontSize="large" />
              </IconButton>
            </Box>

            {/* Price Summary */}
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: "#F0F8FF",
                border: "1px solid #D0E8FF",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                  {quantity} × {formatPrice(pricePerUnit)}
                </Typography>
                <Typography
                  sx={{ fontSize: 18, fontWeight: 800, color: "#1A57D3" }}
                >
                  {formatPrice(newTotalPrice)}
                </Typography>
              </Box>

              {priceDiff !== 0 && (
                <Typography
                  sx={{
                    fontSize: 13,
                    color: priceDiff > 0 ? "#8A6E00" : "#2E7D32",
                    textAlign: "right",
                  }}
                >
                  {priceDiff > 0 ? "+" : ""}
                  {formatPrice(priceDiff)} per delivery
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Footer */}
      {!success && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: "1px solid rgba(0,0,0,.08)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <QuizButton
              variant="secondary"
              onClick={() => onClose(false)}
              disabled={confirming}
              fullWidth
            >
              Cancel
            </QuizButton>
            <QuizButton
              variant="primary"
              onClick={handleConfirm}
              disabled={confirming || quantity === currentQuantity}
              fullWidth
            >
              {confirming ? "Updating..." : "Update Quantity"}
            </QuizButton>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
